import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, UserCheck, Plus, Printer, PackageCheck } from 'lucide-react';
import { AssignmentForm } from '../AssignmentForm/AssignmentForm';
import { AssignFromEntryModal } from './AssignFromEntryModal';
import { printMovement } from '../print/printMovement';
import { api } from '../../services/api';
import { mapAssignmentApiRow } from './assignmentListMappers';

const ASSIGNMENT_PAGE_SIZE = 50;

export const AssignmentModule = ({ materials, people, assignments, brandItems, movements, warehouses = [
  { id: 'popayan', name: 'Popayán' },
  { id: 'santander', name: 'Santander de Quilichao' },
], onRegister, onReturn, initialType = 'Asignación' }) => {
  const [showForm, setShowForm] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [returnedKeys, setReturnedKeys] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [assignmentListRefreshKey, setAssignmentListRefreshKey] = useState(0);
  const [assignmentRows, setAssignmentRows] = useState([]);
  const [assignmentHasMore, setAssignmentHasMore] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentLoadingMore, setAssignmentLoadingMore] = useState(false);
  const [assignmentListError, setAssignmentListError] = useState('');
  const assignmentRequestIdRef = useRef(0);
  const assignmentInFlightRef = useRef(false);
  const assignmentRowsRef = useRef([]);
  const assignmentHasMoreRef = useRef(false);
  const assignmentScrollRef = useRef(null);
  const assignmentSentinelRef = useRef(null);

  const isLoan = initialType === 'Préstamo';

  const loanList = useMemo(() => {
    const base = (movements || [])
      .filter(m => (m.type || '').toLowerCase() === 'prestamo' || (m.type || '').toLowerCase() === 'préstamo')
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    return base.filter((m) => {
      const key = (m.serial || '').trim()
        ? `${String(m.materialId)}|${String(m.serial).trim()}`
        : `id:${String(m.id)}`;
      return !returnedKeys.has(key);
    }).slice(0, 50);
  }, [movements, returnedKeys]);

  const fetchAssignmentPage = useCallback(async (offset, { append = false } = {}) => {
    if (append && (assignmentInFlightRef.current || !assignmentHasMoreRef.current)) return;
    const requestId = ++assignmentRequestIdRef.current;
    assignmentInFlightRef.current = true;
    if (append) setAssignmentLoadingMore(true);
    else {
      setAssignmentLoading(true);
      if (assignmentScrollRef.current) assignmentScrollRef.current.scrollTop = 0;
    }
    setAssignmentListError('');
    try {
      const res = await api.assignments.get({
        limit: ASSIGNMENT_PAGE_SIZE,
        offset,
        order: 'desc',
        active_only: 1,
      });
      if (requestId !== assignmentRequestIdRef.current) return;
      const page = (Array.isArray(res?.data) ? res.data : []).map(mapAssignmentApiRow);
      const more = page.length === ASSIGNMENT_PAGE_SIZE;
      assignmentHasMoreRef.current = more;
      setAssignmentHasMore(more);
      setAssignmentRows((prev) => {
        const next = append ? [...prev, ...page] : page;
        assignmentRowsRef.current = next;
        return next;
      });
    } catch (err) {
      if (requestId !== assignmentRequestIdRef.current) return;
      console.error('Error cargando asignaciones activas:', err);
      setAssignmentListError('No se pudo cargar las asignaciones. Revisa tu conexión.');
      if (!append) {
        assignmentRowsRef.current = [];
        setAssignmentRows([]);
      }
      assignmentHasMoreRef.current = false;
      setAssignmentHasMore(false);
    } finally {
      if (requestId === assignmentRequestIdRef.current) {
        assignmentInFlightRef.current = false;
        setAssignmentLoading(false);
        setAssignmentLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    if (isLoan) return;
    fetchAssignmentPage(0, { append: false });
  }, [fetchAssignmentPage, isLoan, assignmentListRefreshKey]);

  useEffect(() => {
    if (isLoan) return undefined;
    const root = assignmentScrollRef.current;
    const sentinel = assignmentSentinelRef.current;
    if (!root || !sentinel) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      if (assignmentInFlightRef.current || !assignmentHasMoreRef.current) return;
      const offset = assignmentRowsRef.current.length;
      if (!offset) return;
      fetchAssignmentPage(offset, { append: true });
    }, { root, rootMargin: '80px', threshold: 0 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchAssignmentPage, isLoan, assignmentRows.length, assignmentLoading]);

  const activeList = isLoan ? loanList : assignmentRows;
  const bumpAssignmentList = () => {
    if (!isLoan) setAssignmentListRefreshKey((k) => k + 1);
  };

  const title = isLoan ? 'Préstamo de Materiales' : 'Asignación de Materiales';
  const listTitle = isLoan ? 'Préstamos Recientes' : 'Asignaciones Activas';
  const buttonText = initialType === 'Préstamo' ? 'Nuevo Préstamo' : 'Nueva Asignación';

  const printReceipt = (r) => {
    printMovement(r);
  };

  const handleSave = async (assignmentData) => {
    // Soporta guardado de lote: si viene items, iteramos y registramos cada uno
    if (Array.isArray(assignmentData.items) && assignmentData.items.length > 0) {
      let ok = 0;
      let fail = 0;
      const failMessages = [];
      let lastMvId = null;
      for (const item of assignmentData.items) {
        try {
          // Asegurar que el tipo se propague a cada ítem individual
          const r = await onRegister({ ...item, type: assignmentData.type });
          if (r && r.success) {
            ok += 1;
            lastMvId = r.movementId || r.movement_id || lastMvId;
          } else {
            fail += 1;
            const msg = r?.message ? String(r.message) : 'Error desconocido al registrar asignación';
            failMessages.push(`${item.materialName || item.materialId} → ${item.responsibleName || item.responsibleDoc}: ${msg}`);
            console.warn('Fallo al registrar ítem de asignación:', msg, item);
          }
        } catch (e) {
          fail += 1;
          const msg = e?.message ? String(e.message) : 'Excepción al registrar asignación';
          failMessages.push(`${item.materialName || item.materialId} → ${item.responsibleName || item.responsibleDoc}: ${msg}`);
          console.warn('Error al registrar ítem de asignación:', msg, item);
        }
      }
      alert(`Asignaciones registradas: ${ok}. Fallidas: ${fail}.` + (failMessages.length ? `\n\nDetalles:\n- ${failMessages.join('\n- ')}` : ''));
      setShowForm(false);
      if (ok > 0) {
        const last = assignmentData.items[assignmentData.items.length - 1];
        const material = materials.find(m => String(m.id) === String(last.materialId));
        const person = people.find(p => String(p.dni || p.id) === String(last.responsibleDoc));
        const wh = warehouses.find(w => w.id === (last.warehouseId || ''));

        // Construir arreglo de ítems para impresión dinámica
        const itemsForPrint = assignmentData.items.map((it) => {
          const mat = materials.find(m => String(m.id) === String(it.materialId));
          return {
            materialName: mat?.name || it.materialName || String(it.materialId),
            materialCode: mat?.code || '',
            materialUnit: mat?.unit || 'Unidad',
            material_category: mat?.category || mat?.type || '',
            unit: mat?.unit || 'Unidad',
            quantity: it.quantity,
            serial: (it.serial || '').trim(),
            brandName: (it.brandName || '').trim(),
          };
        });

        const receipt = {
          type: assignmentData.type || 'Asignación',
          date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
          materialName: material?.name || last.materialId,
          materialCode: material?.code,
          material_category: material?.category || material?.type || '',
          materialUnit: material?.unit || 'Unidad',
          unit: material?.unit || 'Unidad',
          materialLocation: material?.location,
          materialStatus: material?.status,
          serial: last.serial || '',
          responsibleName: person?.name || last.responsibleDoc,
          responsibleDoc: person?.dni || last.responsibleDoc,
          quantity: last.quantity,
          observations: last.observations,
          optionalItems: (last.optionalItems || '').trim(),
          evidencePhotos: Array.isArray(last.evidencePhotos) ? last.evidencePhotos : [],
          warehouseId: last.warehouseId,
          warehouseName: wh?.name || last.warehouseId || '',
          // ítems para impresión del comprobante (tabla dinámica)
          items: itemsForPrint,
          entryRef: lastMvId ? `MV-${lastMvId}` : undefined
        };
        // Adjunta firma digital si existe en el registro de la persona
        if (person?.signature) { receipt.signatureData = person.signature; }
        setLastReceipt(receipt);
        setTimeout(() => { try { printMovement(receipt); } catch (_) {} }, 10);
        bumpAssignmentList();
      }
      return;
    }

    const result = await onRegister(assignmentData);
    if (result && result.success) {
      alert(result.message);
      const material = materials.find(m => String(m.id) === String(assignmentData.materialId));
      const person = people.find(p => String(p.dni || p.id) === String(assignmentData.responsibleDoc));
      const wh = warehouses.find(w => w.id === (assignmentData.warehouseId || ''));
      const receipt = {
        type: assignmentData.type || 'Asignación',
        date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
        materialName: material?.name || assignmentData.materialId,
        materialCode: material?.code,
        material_category: material?.category || material?.type || '',
        materialUnit: material?.unit || 'Unidad',
        unit: material?.unit || 'Unidad',
        materialLocation: material?.location,
        materialStatus: material?.status,
        serial: assignmentData.serial || '',
        responsibleName: person?.name || assignmentData.responsibleDoc,
        responsibleDoc: person?.dni || assignmentData.responsibleDoc,
        quantity: assignmentData.quantity,
        observations: assignmentData.observations,
        optionalItems: (assignmentData.optionalItems || '').trim(),
        evidencePhotos: Array.isArray(assignmentData.evidencePhotos) ? assignmentData.evidencePhotos : [],
        warehouseId: assignmentData.warehouseId,
        warehouseName: wh?.name || assignmentData.warehouseId || '',
        entryRef: result.movementId ? `MV-${result.movementId}` : undefined
      };
      if (person?.signature) { receipt.signatureData = person.signature; }
      setLastReceipt(receipt);
      setTimeout(() => { try { printMovement(receipt); } catch (_) {} }, 10);
      setShowForm(false);
      bumpAssignmentList();
    } else {
      alert(result?.message || 'No fue posible registrar la asignación.');
    }
  };

  const handleReturnLoan = async (mv) => {
    try {
      if (!onReturn) { alert('No hay función de devolución configurada.'); return; }
      const serial = String(mv?.serial || '').trim();
      const serieLine = serial ? `\nSerie: ${serial}` : '';
      const ok = window.confirm(`¿Confirmas devolver este préstamo?\n\n${mv.materialName || mv.materialId}${serieLine}\nResponsable: ${mv.responsibleName || mv.responsibleDoc}\nBodega: ${mv.warehouseId || ''}`);
      if (!ok) return;

      const returnData = {
        assignmentId: '0',
        assignmentDbId: null,
        responsibleDoc: mv.responsibleDoc || '',
        responsibleName: mv.responsibleName || '',
        materialId: mv.materialId,
        materialName: mv.materialName || '',
        serial,
        brandName: String(mv?.brandName || '').trim(),
        assignedQuantity: Number(mv.quantity) || 1,
        returnQuantity: Number(mv.quantity) || 1,
        warehouseId: mv.warehouseId || 'popayan',
        materialState: 'Bueno',
        observations: 'Devolución desde historial de Préstamos'
      };

      const result = await onReturn(returnData);
      if (!result?.success) {
        alert(result?.message || 'No fue posible registrar la devolución.');
        return;
      }

      const material = materials.find(m => String(m.id) === String(returnData.materialId));
      const person = people.find(p => String(p.dni || p.cedula || p.documento_identidad || p.documento || p.doc || p.id) === String(returnData.responsibleDoc));
      const wh = warehouses.find(w => w.id === (returnData.warehouseId || ''));

      const receipt = {
        type: 'Devolución',
        date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
        materialName: material?.name || returnData.materialName || returnData.materialId,
        materialCode: material?.code,
        material_category: material?.category || material?.type || '',
        materialUnit: material?.unit || 'Unidad',
        unit: material?.unit || 'Unidad',
        materialLocation: material?.location,
        materialStatus: material?.status,
        serial: returnData.serial,
        brandName: returnData.brandName,
        responsibleName: person?.name || returnData.responsibleName || returnData.responsibleDoc,
        responsibleDoc: person?.dni || person?.cedula || person?.documento_identidad || returnData.responsibleDoc,
        quantity: returnData.returnQuantity,
        observations: `${returnData.observations || ''}`.trim(),
        warehouseId: returnData.warehouseId,
        warehouseName: wh?.name || returnData.warehouseId || '',
        entryRef: result.movementId ? `MV-${result.movementId}` : undefined,
        items: [{
          materialName: material?.name || returnData.materialName || String(returnData.materialId),
          materialCode: material?.code || '',
          materialUnit: material?.unit || 'Unidad',
          material_category: material?.category || material?.type || '',
          unit: material?.unit || 'Unidad',
          quantity: returnData.returnQuantity,
          serial: returnData.serial,
          brandName: returnData.brandName,
        }]
      };
      if (person?.signature) { receipt.signatureData = person.signature; }
      setLastReceipt(receipt);
      setTimeout(() => { try { printMovement(receipt); } catch (_) {} }, 10);

      const key = String(returnData.serial || '').trim()
        ? `${String(returnData.materialId)}|${String(returnData.serial).trim()}`
        : `id:${String(mv?.id)}`;
      setReturnedKeys((prev) => new Set([...Array.from(prev.values()), key]));
      alert('Devolución registrada exitosamente.');
    } catch (e) {
      alert(e?.message || 'Error al realizar la devolución.');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="section-title mb-4">{title}</h2>

      {lastReceipt && !showForm && (
        <div className="mb-4 p-3 border rounded bg-gray-50 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Movimiento registrado: {lastReceipt.type}</p>
            <p className="text-xs text-gray-600">{lastReceipt.materialName} → {lastReceipt.responsibleName} — Cant: {lastReceipt.quantity} — {lastReceipt.date}</p>
          </div>
          <button onClick={() => printReceipt(lastReceipt)} className="bg-gray-800 text-white px-3 py-2 rounded flex items-center gap-2">
            <Printer size={16} /> Imprimir comprobante
          </button>
        </div>
      )}

      {!showForm ? (
        <div className="text-center py-12">
          <UserCheck size={64} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-4">Asigne materiales a una persona o a un vehículo (dotación carro)</p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-500 text-white px-6 py-3 rounded flex items-center gap-2 hover:bg-blue-600"
            >
              <Plus size={20} /> {buttonText}
            </button>
            {!isLoan && (
              <button
                type="button"
                onClick={() => setBulkAssignOpen(true)}
                className="bg-green-600 text-white px-6 py-3 rounded flex items-center gap-2 hover:bg-green-700"
              >
                <PackageCheck size={20} /> Asignar cargue
              </button>
            )}
          </div>
        </div>
      ) : (
        <AssignmentForm
          materials={materials}
          people={people}
          assignments={assignments}
          brandItems={brandItems}
          movements={movements}
          warehouses={warehouses}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
          initialType={initialType}
        />
      )}

      {!showForm && (isLoan || assignmentLoading || activeList.length > 0 || assignmentListError) && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h3 className="text-lg font-bold">{listTitle}</h3>
            {!isLoan && (
              <span className="text-xs text-gray-500">
                {(assignmentLoading || assignmentLoadingMore)
                  ? 'Cargando…'
                  : assignmentListError
                    ? assignmentListError
                    : `Mostrando ${activeList.length}${assignmentHasMore ? '+' : ''}`}
              </span>
            )}
          </div>
          <div
            ref={isLoan ? undefined : assignmentScrollRef}
            className={isLoan ? 'overflow-x-auto' : 'overflow-auto border rounded-lg max-h-[32rem]'}
          >
            <table className="w-full min-w-full text-sm">
              <thead className="bg-gray-200">
                <tr>
                  <th className="p-2 text-left">Material</th>
                  <th className="p-2 text-left">Responsable</th>
                  <th className="p-2 text-center">Cantidad</th>
                  <th className="p-2 text-left">Fecha</th>
                  <th className="p-2 text-center">Evidencias</th>
                  <th className="p-2 text-left">Estado</th>
                  {isLoan && <th className="p-2 text-left">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {!isLoan && assignmentLoading && activeList.length === 0 && (
                  <tr>
                    <td className="p-4 text-center text-gray-500" colSpan={6}>Cargando…</td>
                  </tr>
                )}
                {!isLoan && !assignmentLoading && activeList.length === 0 && (
                  <tr>
                    <td className="p-4 text-center text-gray-500" colSpan={6}>
                      {assignmentListError || 'No hay asignaciones activas.'}
                    </td>
                  </tr>
                )}
                {activeList.map(item => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">{item.materialName || item.materialId}</td>
                    <td className="p-2">{item.responsibleName || item.responsibleDoc}</td>
                    <td className="p-2 text-center">{item.quantity}</td>
                    <td className="p-2">{item.assignDate || item.date}</td>
                    <td className="p-2 text-center">{Array.isArray(item.evidencePhotos) ? item.evidencePhotos.length : 0}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${isLoan ? 'bg-indigo-200 text-indigo-800' : 'bg-blue-200 text-blue-800'}`}>
                        {item.status || item.type}
                      </span>
                    </td>
                    {isLoan && (
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => handleReturnLoan(item)}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm flex items-center gap-2 hover:bg-green-700"
                        >
                          <RotateCcw size={16} /> Devolver
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!isLoan && assignmentLoadingMore && (
                  <tr>
                    <td className="p-4 text-center text-gray-500" colSpan={6}>Cargando más…</td>
                  </tr>
                )}
              </tbody>
            </table>
            {!isLoan && <div ref={assignmentSentinelRef} className="h-2" aria-hidden="true" />}
          </div>
        </div>
      )}

      {bulkAssignOpen && (
        <AssignFromEntryModal
          materials={materials}
          people={people}
          warehouses={warehouses}
          onClose={() => setBulkAssignOpen(false)}
          onConfirm={async (items) => {
            const first = items[0] || {};
            await handleSave({
              items,
              warehouseId: first.warehouseId,
              type: 'Asignación',
              assigneeType: first.assigneeType || 'person',
              vehicleId: first.vehicleId || null,
            });
            setBulkAssignOpen(false);
          }}
        />
      )}
    </div>
  );
};
